import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, RefreshCw } from "lucide-react";

export default function SwitchOrder() {
  const navigate = useNavigate();
  const [holdings, setHoldings] = useState([]);
  const [holdingKey, setHoldingKey] = useState("");
  const [fromScheme, setFromScheme] = useState("");
  const [toSearch, setToSearch] = useState("");
  const [toResults, setToResults] = useState([]);
  const [toScheme, setToScheme] = useState("");
  const [amount, setAmount] = useState("");
  const [units, setUnits] = useState("");
  const [switchMode, setSwitchMode] = useState("amount");
  const [folio, setFolio] = useState("");
  const [demat, setDemat] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const applyHolding = (row) => {
    if (!row) return;
    setHoldingKey(row.holding_key || row.scheme_code);
    setFromScheme(row.scheme_code);
    setFolio(row.folio_no || "");
    setDemat(row.holding_mode === "DEMAT");
  };

  useEffect(() => {
    clientApi.get("/portfolio/holdings").then((r) => {
      const rows = (r.data.holdings || []).filter((h) => Number(h.units) > 0);
      setHoldings(rows);
      if (rows.length > 0) applyHolding(rows[0]);
    }).catch((err) => toast.error(getApiErrorMessage(err)));
  }, []);

  useEffect(() => {
    if (!toSearch) { setToResults([]); return; }
    const t = setTimeout(() => {
      clientApi.get("/schemes", { params: { search: toSearch, switch_flag: true, page_size: 6 } }).then((r) => setToResults(r.data.items));
    }, 300);
    return () => clearTimeout(t);
  }, [toSearch]);

  const handleSwitch = async (e) => {
    e.preventDefault();
    if (!toScheme) { toast.error("Please select a destination scheme"); return; }
    setLoading(true);
    try {
      const { data } = await clientApi.post("/orders/switch", {
        from_scheme_code: fromScheme, to_scheme_code: toScheme,
        amount: switchMode === "amount" ? Number(amount) : undefined,
        units: switchMode === "units" ? Number(units) : undefined,
        all_units: switchMode === "all",
        folio_no: folio, demat,
      });
      const pending = ["SUBMITTING", "UNKNOWN"].includes(data.bse2?.status);
      setResult({ ...data, confirmationPending: pending });
      if (pending) toast.warning(data.bse2?.message || "BSE confirmation is pending. Do not submit the switch again.");
      else toast.success(data.bse2?.message || "Switch order accepted by BSE.");
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <ClientLayout>
        <div className="mx-auto max-w-lg py-10 text-center">
          <CheckCircle2 className="mx-auto h-16 w-16 text-[var(--cta)]" />
          <h1 className="mt-4 font-heading text-2xl text-[var(--brand)]">{result.confirmationPending ? "Switch confirmation pending" : "Switch order submitted"}</h1>
          <p className="mt-2 text-sm text-[var(--ink-muted)]">Order ID #{result.order_id}</p>
          {result.bse2?.auth_link && <a href={result.bse2.auth_link} target="_blank" rel="noreferrer" className="mt-4 block text-sm font-medium text-[var(--cta)] underline">Complete authentication on BSE →</a>}
          {result.bse2 && <p className="mt-2 text-xs text-[var(--ink-muted)]">{result.bse2.message}</p>}
          <Button className="mt-6 rounded-full bg-[var(--cta)]" onClick={() => navigate("/orders")} data-testid="switch-result-view-orders-button">View orders</Button>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="mx-auto max-w-xl">
        <div className="mb-6 flex items-center gap-3">
          <RefreshCw className="h-6 w-6 text-[var(--cta)]" />
          <h1 className="font-heading text-2xl text-[var(--brand)]">Switch between schemes</h1>
        </div>
        <Card className="glass-card border-0">
          <CardHeader><CardTitle className="font-heading text-lg text-[var(--brand)]">Switch request</CardTitle></CardHeader>
          <CardContent>
            {holdings.length === 0 ? (
              <div className="text-sm text-[var(--ink-muted)]" data-testid="switch-no-holdings-message">No allotted units are available to switch. Refresh your BSE portfolio from the Orders page if an allotment is missing.</div>
            ) : (
              <form className="space-y-5" onSubmit={handleSwitch}>
                <div className="space-y-1.5">
                  <Label>Switch from holding</Label>
                  <select className="w-full rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={holdingKey} onChange={(e) => applyHolding(holdings.find((h) => (h.holding_key || h.scheme_code) === e.target.value))} data-testid="switch-from-select">
                    {holdings.map((h) => <option key={h.holding_key || h.scheme_code} value={h.holding_key || h.scheme_code}>{h.scheme_name} · folio {h.folio_no || "—"} · {h.holding_mode || "PHYSICAL"} · {h.units} units</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3 rounded-xl bg-[var(--surface-2)] p-3 text-xs"><div><span className="text-[var(--ink-muted)]">Folio</span><div className="font-medium">{folio || "—"}</div></div><div><span className="text-[var(--ink-muted)]">Holding mode</span><div className="font-medium">{demat ? "DEMAT" : "PHYSICAL"}</div></div></div>
                <div className="space-y-1.5">
                  <Label>Switch to (search)</Label>
                  <Input placeholder="Search destination scheme..." value={toSearch} onChange={(e) => setToSearch(e.target.value)} data-testid="switch-to-search-input" />
                  {toResults.length > 0 && (
                    <div className="max-h-48 overflow-y-auto rounded-md border border-[var(--glass-border)]">
                      {toResults.map((s) => (
                        <button
                          type="button"
                          key={s.scheme_code}
                          onClick={() => { setToScheme(s.scheme_code); setToSearch(s.scheme_name); setToResults([]); }}
                          className="block w-full p-2 text-left text-xs hover:bg-[var(--surface-2)]"
                          data-testid={`switch-to-option-${s.scheme_code}`}
                        >
                          {s.scheme_name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Switch instruction</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[["amount", "Amount"], ["units", "Units"], ["all", "All units"]].map(([key, label]) => (
                      <Button key={key} type="button" size="sm" variant={switchMode === key ? "default" : "outline"}
                        className="rounded-full" onClick={() => setSwitchMode(key)}>{label}</Button>
                    ))}
                  </div>
                </div>
                {switchMode === "amount" && <div className="space-y-1.5">
                  <Label>Switch amount (INR)</Label>
                  <Input type="number" min="0.01" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="switch-amount-input" />
                </div>}
                {switchMode === "units" && <div className="space-y-1.5">
                  <Label>Units to switch</Label>
                  <Input type="number" min="0.0001" step="0.0001" required value={units} onChange={(e) => setUnits(e.target.value)} data-testid="switch-units-input" />
                </div>
                }
                <Button type="submit" disabled={loading} className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="switch-submit-button">
                  {loading ? "Submitting..." : "Submit switch"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </ClientLayout>
  );
}
